import { Component, Input, SimpleChanges } from '@angular/core';
import { GamesParameterHistoryService } from '../../../../services/games-parameter-history.service';
import { GameType } from '../../../../models/games.model';
import { IHGameStandardize, IHSelectedMetric } from '../../../../models/games-parameter.model';

export const MilestoneThemes = [
    {
        themeID: 1,
        themePath: "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/space/",
    },
    {
        themeID: 2,
        themePath: "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/pirate/",
    },
    {
        themeID: 3,
        themePath: "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/racing/",
    },
    {
        themeID: 4,
        themePath: "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/camping/",
    },
    {
        themeID: 5,
        themePath: "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/snow/",
    },
    {
        themeID: 6,
        themePath: "../../../../../App_Themes/Standard/Images/CoreTheme/Gamification/Games/MilestoneThemes/hiking/",
    }
];

@Component({
    selector: 'cv-health-report-specific-details',
    templateUrl: './health-report-specific-details.component.html',
    styleUrl: './health-report-specific-details.component.scss'
})
export class HealthReportSpecificDetailsComponent {
    @Input() game = null;
    @Input() endDate: string = null;
    @Input() gameActions = null;
    @Input() participants: any[];
    @Input() isLoading: boolean = false;
    @Input() historyGame: IHGameStandardize = null;

    metric: IHSelectedMetric;
    milestoneTheme = null;
    extraRewardDetails: Map<number, number> = new Map();
    topParticipants = [];
    popoverTimeout;
    currentPopover: any;
    hasBadges: boolean;
    constructor(private gamesParameterHistoryService: GamesParameterHistoryService) { }

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.endDate?.currentValue || changes.game?.currentValue) {
            this.game = changes.game?.currentValue || this.game;
            this.endDate = changes.endDate?.currentValue?.split('T')[0] || this.endDate;

            this.hasBadges = (this.game.GameType !== GameType.MultiplierMadness && this.game.GameType !== GameType.Raffle);
            if (this.game?.RuleID && this.endDate != null) {
                this.cleanVariables();
                this.getRewardDetails();
            }
        }

        if (changes.gameActions && changes.gameActions.currentValue != null) {
            this.gameActions = changes.gameActions?.currentValue || this.gameActions;
            this.getExtraDetails();
        }

        if (changes.participants && changes.participants.currentValue != null) {
            if (this.game?.GameType === GameType.MultiplierMadness || this.game?.GameType === GameType.TopDog || this.game?.GameType === GameType.TopTeam) {
                this.metric = this.getMetricDetails();

                if (this.game?.GameType !== GameType.MultiplierMadness && this.metric != null) {
                    this.participants = changes.participants?.currentValue || this.participants;
                    if (this.metric.MetricDetail.DefaultMetricDirection == 1) {
                        this.participants = this.participants.sort((a, b) => b.metricValue - a.metricValue);
                    } else {
                        this.participants = this.participants.sort((a, b) => a.metricValue - b.metricValue);
                    }
                    this.getTopParticipants();
                }
            }
        }
    }

    cleanVariables() {
        this.metric = undefined;
        this.milestoneTheme = null;
        this.extraRewardDetails = new Map();
        this.topParticipants = [];
    }

    get areDetailsLoaded() {
        if (this.game && this.endDate && this.gameRewards) {
            switch (this.game?.GameType) {
                case GameType.TopDog:
                case GameType.TopTeam:
                    return !(this.topParticipants && this.metric);
                case GameType.MultiplierMadness:
                    return !(this.metric);
                case GameType.Bullseye:
                case GameType.Milestone:
                    return !(this.extraRewardDetails.size > 0 || this.gameActions?.length == 0);
                default:
                    return false;
            }
        } else if (!this.endDate) {
            return false;
        }
        return true;
    }

    getRewardDetails() {
        if (this.game && this.endDate) {
            this.gameRewards;

            if (this.game?.GameType === GameType.Milestone)
                this.getMilestoneTheme();
        }
    }

    get gameRewards() {
        if (!this.isLoading && this.game?.RuleID && this.historyGame) {
            let result = [...this.historyGame.rewards];

            if (this.game?.GameType === GameType.Milestone) {
                result = result?.slice().reverse();
            }

            if (this.game?.GameType === GameType.Brackets) {
                const rounds = this.game.HistoryBracketTournamentDetails?.NumberOfRounds;
                while (result.length < rounds) {
                    result.push(null); // Fill the rounds without rewards with nulls
                }
            }
            return result;
        }
        return [];
    }

    getMetricDetails() {
        let result = this.gamesParameterHistoryService.getMetrics(this.game.RuleID, this.endDate);
        return result.length > 0 ? result.find(x => !x.IsQualifier) : null;
    }

    getTitle() {
        if (this.gameRewards) {
            switch (this.game?.GameType) {
                case GameType.MultiplierMadness:
                case GameType.Connect3:
                case GameType.Blackout:
                    return this.getLocalizedText("Rewards", "Rewards");
                case GameType.TopDog:
                case GameType.TopTeam:
                    return this.getLocalizedText("Places", "Places");
                case GameType.Bullseye:
                    return this.getLocalizedText("Rings", "Rings");
                case GameType.Milestone:
                    return this.getLocalizedText("Milestones", "Milestones");
                case GameType.Raffle:
                    return this.getLocalizedText("Prizes", "Prizes");
                case GameType.Brackets:
                    return this.getLocalizedText("Rounds", "Rounds");
                default:
                    return '';
            }
        }
    }

    getTopParticipants() {
        if (this.metric) {
            if (this.game?.GameType === GameType.TopDog) {
                const uniqueParticipants = this.participants.filter(item => item.rank !== undefined)
                    .reduce((acc: any, item) => {
                        if (!acc.some(el => el.rank === item.rank)) acc.push(item);
                        return acc;
                    }, []);

                this.participants = this.participants.reduce((acc: any, item) => {
                    if (!acc.some(el => el.metricValue === item.metricValue)) acc.push(item);
                    return acc;
                }, []);
                this.topParticipants = uniqueParticipants.sort((a, b) => a.rank - b.rank);
            } else if (this.game?.GameType === GameType.TopTeam) {
                this.topParticipants = this.getMetricValues(this.participants);
                this.participants = this.topParticipants;
            }            
        }
    }

    getMetricValues(players) {
        const metricDir = this.metric.MetricDetail.DefaultMetricDirection;
        const result = players.reduce((acc: Record<string, { metricValue: number, rank: number | null }>, player) => {
                            const team = player.participant.Team;
                            const { metricValue, rank } = player;
                            if(!acc[team]) { 
                                acc[team] = { metricValue: 0, rank: rank ?? null };
                            }

                            if (metricValue !== undefined)
                                acc[team].metricValue += metricValue;

                            return acc;
                       }, {});

        let playerList = Object.entries(result).map(([team, data]) => ({
                            team,
                            metricValue: data["metricValue"],
                            metric: this.getFormattedData(this.metric.MetricDetail.ConversionType, data["metricValue"]),
                            rank: data["rank"]
                            }));

        playerList = playerList.sort((a, b) => {
            if (a.rank === null && b.rank === null) {
                return metricDir == 1 ? (b.metricValue - a.metricValue) : (a.metricValue - b.metricValue);
            } else if (a.rank == null) {
                return 1;
            } else if (b.rank == null) {
                return -1;
            }

            if (a.rank !== b.rank) {
                return a.rank - b.rank;
            }

            return metricDir ? (b.metricValue - a.metricValue) : (a.metricValue - b.metricValue);
        });

        return playerList;
    }

    getLocalizedTierText(tier: number): string {
        if (!this.metric || !this.metric.MetricDetail) {
            return '---';
        }

        const allRankNull = this.topParticipants.every(item => item.rank === null);
        let metricValue = null;
        
        if (this.topParticipants.length > 0 && !allRankNull) {
            metricValue = this.topParticipants.find(x => x.rank == tier)?.metric ?? '---';
        } else {
            metricValue = this.participants[tier - 1]?.metric ?? '---';
        }

        switch (tier) {
            case 1:
                return `${this.getLocalizedText("First_Place", "1st Place")} - ${metricValue}`;
                break;
            case 2:
                return `${this.getLocalizedText("Second_Place", "2nd Place")} - ${metricValue}`;
                break;
            case 3:
                return `${this.getLocalizedText("Third_Place", "3rd Place")} - ${metricValue}`;
                break;
            default:
                let number = tier + this.getLocalizedText("Th_Symbol", "th") + " ";
                number += `${this.getLocalizedText("Place", "Place")} - ${metricValue}`;
                return number;
        }
    }

    getRangeText(reward) {
        if (!reward?.metricTarget) {
            return '';
        }

        const comparerType = reward.metricTarget.ComparerType;
        switch (comparerType) {
            case 1:
                return `${this.getLocalizedText("Greater_Than", "Greater Than")} ${reward.metricTarget.LeftNumber}`;
            case 2:
                return `${this.getLocalizedText("Less_Than", "Less Than")} ${reward.metricTarget.LeftNumber}`;
            case 3:
                return `${this.getLocalizedText("Between", "Between").replace("{0}", reward.metricTarget.LeftNumber.toString()).replace("{1}", reward.metricTarget.RightNumber.toString())}`;
            default:
                return '';
        }
    }

    getExtraDetails() {
        this.extraRewardDetails = this.gameActions.reduce((map, action) => {
            const key = `${action.ActionLevelID}-${action.LoginID}`;
            if (!map.seen.has(key)) {
                map.seen.add(key);
                map.count.set(action.ActionLevelID, (map.count.get(action.ActionLevelID) || 0) + 1);
            }
            return map;
        }, { seen: new Set<string>(), count: new Map<number, number>() }).count;
    }

    getExtraDetailsText(levelID: string) {
        const level = +levelID;
        const count = this.extraRewardDetails.get(level);

        switch (this.game?.GameType) {
            case GameType.Bullseye:
                if (count == 0 || count == undefined) {
                    return this.getLocalizedText("Not_Hit", "Not Hit");
                } else if (count == 1) {
                    return this.getLocalizedText("Hit_Once", "Hit once");
                } else {
                    return this.getLocalizedText("Hit_N_Times", "Hit {0} times").replace("{0}", count.toString());
                }
                break;
            case GameType.Milestone:
                if (count == 0 || count == undefined) {
                    return this.getLocalizedText("Not_Reached", "Not Reached");
                } else if (count == 1) {
                    return this.getLocalizedText("Reached_Once", "Reached once");
                } else {
                    return this.getLocalizedText("Reached_N_Times", "Reached {0} times").replace("{0}", count.toString());
                }
                break;
        }
    }

    getMilestoneImage(index: number) {
        if (!this.milestoneTheme || !this.milestoneTheme.themePath) {
            return {};
        }
    
        return { 'background-image': `url(${this.milestoneTheme.themePath}checkmark${index}.svg)` }
    }

    getMilestoneTheme() {
        this.milestoneTheme = MilestoneThemes.find(t => t.themeID == this.game.MilestoneDetails.MilestoneThemeSelected.themeID);
    }

    getLocalizedMultiplierMadnessText(isMetricTitle: boolean) {
        let title = '';
        if (isMetricTitle) {
            title = this.getLocalizedText("Rewards_Per", "Rewards Per {0}");
            title = title.replace("{0}", this.metric.MetricDetail["Name"]);
        } else {
            title = this.getLocalizedText("Max_Payout_Possible", "Max Payout Possible");
        }
        return title;
    }

    getBracketRoundName(round: number) {
        switch (round) {
            case 1:
                return this.getLocalizedText("Round_1_Final", "Final");
                break;
            case 2:
                return this.getLocalizedText("Round_2_Semi_Finals", "Semi Finals");
                break;
            case 3:
                return this.getLocalizedText("Round_3_Quarter", "Quarters");
                break;
            case 4:
                return this.getLocalizedText("Round_4_Octaves", "Octaves");
                break;
        }
    }

    getConnectGamesText(isConnect3: boolean) {
        if (isConnect3) {
            return this.getLocalizedText("Three_In_Row", "3 in a row");
        } else {
            return this.getLocalizedText("Blackout", "Connect All");
        }
    }

    getRafflePrizeText(index: number) {
        return `${this.getLocalizedText("Prize", "Prize")} ${index}`;
    }

    openPopover(popover) {
        if (this.popoverTimeout) {
            clearTimeout(this.popoverTimeout);
            this.popoverTimeout = null;
        }

        this.popoverTimeout = setTimeout(() => {
            this.currentPopover = popover;
            popover.open();
        }, 500);
    }

    cancelTimeout() {
        clearTimeout(this.popoverTimeout);
    }

    closePopover() {
        this.currentPopover?.close();
        this.cancelTimeout();
    }

    getLocalizedText(key: string, defaultText: string): string {
        return this.gamesParameterHistoryService.getLocalizationMessage(key, defaultText);
    }

    getFormattedData(conversionType: number, value: number): any {
        let formatted: any;
        if (value == undefined || value == null) value = 0;

        switch (conversionType) {
            case 1: //Second
                formatted = value;
                break;
            case 2: //Minute
                formatted = value / 60;
                break;
            case 3: //Hour
                formatted = Math.floor((value / 3600) * 100) / 100;
                break;
            case 4: //Time
                let hours = parseInt(Math.floor(value / 3600).toString());
                let minutes = parseInt(Math.floor((value - (hours * 3600)) / 60).toString());
                let seconds = parseInt((value - (hours * 3600) - (minutes * 60)).toString());

                let hh = (hours < 10) ? "0" + hours : hours;
                let mm = (minutes < 10) ? "0" + minutes : minutes;
                let ss = (seconds < 10) ? "0" + seconds : seconds;

                formatted = hh + ':' + mm + ':' + ss;
                break;
            case 5: //Percent
                formatted = `${(value * 100).toFixed(2)}%`;
                break;
            default:
                formatted = value;
                break;
        }

        return formatted;
    }
}
